import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Clock, MessageSquare, FileText, LogOut, Download, Send, Phone, Wrench, Gift, Copy } from "lucide-react";
import api, { BACKEND_URL, formatDetail, openWhatsApp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import ReviewForm from "@/components/ReviewForm";
import { TIERS, formatINR } from "@/data/content";
import { buildReferralLink } from "@/lib/attribution";

const STAGES = [
  "AWAITING_PAYMENT",
  "PAID_ACTIVATING",
  "BENCHMARK_SENT",
  "NEGOTIATION",
  "PDI_SCHEDULED",
  "PDI_DONE",
  "PAPERWORK",
  "DELIVERY_SCHEDULED",
  "DELIVERED",
  "CLOSED",
];

const LABELS = {
  AWAITING_PAYMENT: "Awaiting payment",
  PAID_ACTIVATING: "Payment received",
  BENCHMARK_SENT: "Benchmark sent",
  NEGOTIATION: "Negotiation",
  PDI_SCHEDULED: "PDI scheduled",
  PDI_DONE: "PDI completed",
  PAPERWORK: "Paperwork",
  DELIVERY_SCHEDULED: "Delivery scheduled",
  DELIVERED: "Delivered",
  CLOSED: "Closed",
};

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const [deal, setDeal] = useState(null);
  const [docs, setDocs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [pdiSessions, setPdiSessions] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  async function load() {
    try {
      const [d, dc, m, ps, rf] = await Promise.all([
        api.get("/user/deal"),
        api.get("/user/documents"),
        api.get("/user/messages"),
        api.get("/user/pdi/sessions").catch(() => ({ data: { sessions: [] } })),
        api.get("/user/referrals").catch(() => ({ data: null })),
      ]);
      setDeal(d.data);
      setDocs(dc.data.documents || []);
      setMessages(m.data.messages || []);
      setPdiSessions(ps.data.sessions || []);
      setReferrals(rf.data);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  async function startPdi() {
    try {
      const { data } = await api.post("/user/pdi/sessions", { car: deal?.car, location: deal?.city });
      nav(`/pdi/${data.id}`);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    }
  }

  function copyReferral() {
    if (!referrals?.code) return;
    navigator.clipboard.writeText(referrals.code);
    toast.success("Referral code copied");
  }

  function copyReferralLink() {
    if (!referrals?.code) return;
    navigator.clipboard.writeText(buildReferralLink(referrals.code));
    toast.success("Referral link copied");
  }

  async function shareReferralLink() {
    if (!referrals?.code) return;
    const link = buildReferralLink(referrals.code);
    const text = "CaarSaarthi helps car buyers negotiate a better deal — buyer-side, never the dealer's side. Use my link to get started:";
    if (navigator.share) {
      try {
        await navigator.share({ title: "CaarSaarthi", text, url: link });
        return;
      } catch {
        // user cancelled or share failed — fall through to copy
      }
    }
    navigator.clipboard.writeText(`${text} ${link}`);
    toast.success("Referral link copied — share it anywhere");
  }

  async function requestPayout(amount) {
    try {
      await api.post("/user/referrals/withdraw", { amount });
      toast.success("Payout requested — our team will review it shortly.");
      load();
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    }
  }

  async function sendMessage(e) {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post("/user/messages", { text });
      setMessages((prev) => [...prev, data]);
      setText("");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    }
  }

  async function onLogout() {
    await logout();
    nav("/");
  }

  const activeIdx = useMemo(() => {
    if (!deal) return -1;
    return STAGES.indexOf(deal.stage);
  }, [deal]);

  const tierMeta = TIERS.find((t) => t.name === deal?.tier);
  const paid = !!deal?.payment;

  return (
    <main className="min-h-screen bg-void text-silver" data-testid="user-dashboard">
      <header className="border-b border-white/5 sticky top-0 bg-void/85 backdrop-blur-md z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full gradient-bg" />
            <span className="font-serif text-xl tracking-tight text-white">CaarSaarthi</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-silver/70 hidden sm:inline">Hi, {user?.name}</span>
            <button onClick={onLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5" data-testid="user-logout"><LogOut className="w-4 h-4"/> Sign out</button>
          </div>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        {loading ? (
          <div className="text-silver/60">Loading your deal…</div>
        ) : !paid ? (
          <UnpaidState deal={deal} name={user?.name} onRequestTier={(t) => setDeal((d) => ({ ...d, requested_tier: t }))} />
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="grid lg:grid-cols-3 gap-6"
              data-testid="user-deal-hero"
            >
              <div className="lg:col-span-2 rounded-3xl gradient-border bg-[#141323] p-8 md:p-10 relative overflow-hidden">
                <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-40 blur-3xl gradient-bg pointer-events-none" />
                <p className="eyebrow gradient-text">Your Active Tier</p>
                <h1 className="mt-3 font-serif text-5xl md:text-6xl text-white leading-none" data-testid="deal-tier-name">
                  {deal.tier}
                </h1>
                <p className="mt-2 text-silver/70">{tierMeta?.tagline || "Buyer-side representation active"}</p>
                <div className="mt-6 flex flex-wrap gap-6 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-silver/50">Car</p>
                    <p className="mt-1 text-white">{deal.car || "TBD"}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-silver/50">City</p>
                    <p className="mt-1 text-white">{deal.city || "TBD"}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-silver/50">Paid</p>
                    <p className="mt-1 text-white">{formatINR(deal.payment.amount)}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-white/5 bg-surface p-8">
                <p className="eyebrow gradient-text">Your Saarthi</p>
                <h3 className="mt-3 font-serif text-3xl text-white leading-tight">{deal.saarthi_name || "Assigning…"}</h3>
                <p className="mt-2 text-silver/60 text-sm">{deal.saarthi_phone}</p>
                {deal.saarthi_phone && (
                  <a
                    href={`https://wa.me/${deal.saarthi_phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${deal.saarthi_name}, ${user?.name} here from CaarSaarthi.`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.02] transition-transform"
                    data-testid="user-whatsapp-saarthi"
                  >
                    <Phone className="w-4 h-4" /> WhatsApp Saarthi
                  </a>
                )}
              </div>
            </motion.div>

            <StageTimeline stage={deal.stage} activeIdx={activeIdx} />

            <div className="mt-10 grid lg:grid-cols-2 gap-6">
              <PDITile unlocked={deal.pdi_unlocked} sessions={pdiSessions} onStart={startPdi} />
              <ReferralCard
                referrals={referrals}
                onCopy={copyReferral}
                onCopyLink={copyReferralLink}
                onShare={shareReferralLink}
                onRequestPayout={requestPayout}
              />
            </div>

            <div className="mt-6 grid lg:grid-cols-2 gap-6">
              <DocsCard docs={docs} />
              <MessagesCard messages={messages} text={text} setText={setText} onSend={sendMessage} />
            </div>

            <div className="mt-6">
              <ReviewCard />
            </div>
          </>
        )}
      </section>
    </main>
  );
}

function UnpaidState({ deal, name, onRequestTier }) {
  const [submitting, setSubmitting] = useState(false);
  const requested = deal?.requested_tier;

  async function pickTier(tierName) {
    if (submitting || requested) return;
    setSubmitting(true);
    try {
      await api.patch("/user/deal/requested-tier", { requested_tier: tierName });
      onRequestTier(tierName);
      toast.success(`Requested ${tierName} — we'll be in touch to confirm payment.`);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Couldn't save your choice");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      className="rounded-3xl gradient-border bg-[#141323] p-10 md:p-14 relative overflow-hidden"
      data-testid="user-unpaid-state"
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-40 blur-3xl gradient-bg pointer-events-none" />
      <p className="eyebrow gradient-text">Ch. I · Welcome, {name}</p>
      <h1 className="mt-4 font-serif text-5xl md:text-7xl text-white leading-[0.95] tracking-tight">
        Your Saarthi is <span className="italic gradient-text">waiting.</span>
      </h1>
      <p className="mt-6 text-silver/70 max-w-2xl leading-relaxed">
        {requested
          ? `You've requested ${requested}. Pay for it on WhatsApp to activate — once payment is received, your admin will confirm the tier and unlock your dashboard.`
          : "Pick the tier you want below, then pay for it on WhatsApp. Once payment is received, your admin will confirm the tier and your dashboard will unlock the deal timeline, documents and direct messaging with your Saarthi."}
      </p>

      {!requested ? (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="unpaid-tier-picker">
          {TIERS.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={submitting || t.comingSoon}
              onClick={() => pickTier(t.name)}
              className="text-left rounded-2xl border border-white/10 bg-surface p-5 hover:border-white/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`unpaid-pick-${t.id}`}
            >
              <p className="font-serif text-xl text-white">{t.name}</p>
              <p className="mt-1 text-xs text-silver/50">{t.priceLabel}</p>
              {t.comingSoon && <p className="mt-2 text-[10px] uppercase tracking-wider text-silver/40">Coming soon</p>}
            </button>
          ))}
        </div>
      ) : (
        <button
          onClick={() => openWhatsApp({ source: "unpaid_state_confirm", tier: requested })}
          className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.02] transition-transform"
          data-testid="unpaid-pay-whatsapp"
        >
          Pay for {requested} on WhatsApp
        </button>
      )}

      <div className="mt-10 grid sm:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-white/5 bg-surface p-5">
          <p className="text-xs uppercase tracking-widest text-silver/50">Status</p>
          <p className="mt-1 font-serif text-2xl text-white">Awaiting payment</p>
        </div>
        <div className="rounded-2xl border border-white/5 bg-surface p-5">
          <p className="text-xs uppercase tracking-widest text-silver/50">Requested Tier</p>
          <p className="mt-1 font-serif text-2xl text-white" data-testid="unpaid-requested-tier">{requested || "Not selected"}</p>
        </div>
        <div className="rounded-2xl border border-white/5 bg-surface p-5">
          <p className="text-xs uppercase tracking-widest text-silver/50">Saarthi</p>
          <p className="mt-1 font-serif text-2xl text-white">TBA</p>
        </div>
      </div>
    </motion.div>
  );
}

function StageTimeline({ stage, activeIdx }) {
  return (
    <div className="mt-10 rounded-3xl border border-white/5 bg-surface p-6 md:p-10" data-testid="deal-timeline">
      <div className="flex items-center justify-between">
        <div>
          <p className="eyebrow gradient-text">Deal Timeline</p>
          <h3 className="mt-2 font-serif text-2xl md:text-3xl text-white">Your Saarthi's next move</h3>
        </div>
        <div className="hidden sm:block text-xs text-silver/50 uppercase tracking-widest">
          Current: <span className="text-white">{LABELS[stage] || stage}</span>
        </div>
      </div>
      <ol className="mt-8 relative">
        <div className="absolute left-4 top-2 bottom-2 w-px bg-white/10" />
        {STAGES.map((s, i) => {
          const done = i < activeIdx;
          const current = i === activeIdx;
          return (
            <li key={s} className="relative pl-12 py-3 flex items-center" data-testid={`stage-${s}`}>
              <span className={`absolute left-0 top-3.5 w-8 h-8 rounded-full grid place-items-center border ${current ? "gradient-bg border-transparent" : done ? "bg-cyan-500/20 border-cyan-500/40 text-cyan-300" : "bg-white/5 border-white/10 text-silver/50"}`}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : current ? <Clock className="w-4 h-4 text-white" /> : <span className="text-xs">{i + 1}</span>}
              </span>
              <span className={`text-sm md:text-base ${current ? "text-white font-medium" : done ? "text-silver/80" : "text-silver/50"}`}>
                {LABELS[s]}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ReviewCard() {
  async function handleSubmit(form) {
    await api.post("/user/reviews", { rating: form.rating, text: form.text, car: form.car, designation: form.designation, photo: form.photo });
  }
  return (
    <div data-testid="user-review-card">
      <p className="eyebrow gradient-text mb-4">Leave a Review</p>
      <ReviewForm fields={["car"]} onSubmit={handleSubmit} submitLabel="Submit review" />
    </div>
  );
}

function DocsCard({ docs }) {
  return (
    <div className="rounded-3xl border border-white/5 bg-surface p-6 md:p-8" data-testid="user-documents">
      <div className="flex items-center justify-between">
        <div>
          <p className="eyebrow gradient-text">Your Documents</p>
          <h3 className="mt-2 font-serif text-2xl text-white">Quotes · PDI · Paperwork</h3>
        </div>
        <FileText className="w-5 h-5 text-silver/50" />
      </div>
      <ul className="mt-6 divide-y divide-white/5">
        {docs.length === 0 && <li className="py-6 text-silver/50 text-sm">No documents yet. Your Saarthi will upload as the deal progresses.</li>}
        {docs.map((d) => (
          <li key={d.id} className="py-4 flex items-center justify-between gap-4" data-testid={`doc-row-${d.id}`}>
            <div className="min-w-0">
              <p className="text-white truncate">{d.title || d.filename}</p>
              <p className="text-xs text-silver/50">{(d.size / 1024).toFixed(0)} KB · {new Date(d.created_at).toLocaleDateString()}</p>
            </div>
            <a
              href={`${BACKEND_URL}/api/user/documents/${d.id}/download`}
              onClick={(e) => {
                e.preventDefault();
                const t = localStorage.getItem("cs_token");
                fetch(`${BACKEND_URL}/api/user/documents/${d.id}/download`, { headers: { Authorization: `Bearer ${t}` } })
                  .then((r) => r.blob())
                  .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = d.filename; a.click();
                    URL.revokeObjectURL(url);
                  });
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-xs hover:bg-white/5 text-white"
              data-testid={`doc-download-${d.id}`}
            >
              <Download className="w-3.5 h-3.5" /> Download
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MessagesCard({ messages, text, setText, onSend }) {  return (
    <div className="rounded-3xl border border-white/5 bg-surface p-6 md:p-8 flex flex-col" data-testid="user-messages">
      <div className="flex items-center justify-between">
        <div>
          <p className="eyebrow gradient-text">Messages</p>
          <h3 className="mt-2 font-serif text-2xl text-white">Talk to your Saarthi</h3>
        </div>
        <MessageSquare className="w-5 h-5 text-silver/50" />
      </div>
      <div className="mt-6 flex-1 min-h-[240px] max-h-[380px] overflow-auto space-y-3 pr-1">
        {messages.length === 0 && <p className="text-silver/50 text-sm">No messages yet. Say hello.</p>}
        {messages.map((m) => {
          const mine = m.from_role === "user";
          const isSystem = m.from_role === "system";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "gradient-bg text-white" : isSystem ? "bg-white/5 text-silver/70 italic" : "bg-void text-silver border border-white/10"}`}>
                {!mine && !isSystem && <p className="text-[10px] uppercase tracking-widest text-silver/50 mb-1">{m.from_name}</p>}
                <p className="whitespace-pre-wrap">{m.text}</p>
                <p className="mt-1 text-[10px] text-white/60">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={onSend} className="mt-4 flex gap-2" data-testid="message-form">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ask your Saarthi anything…"
          className="bg-void border-white/10 h-11 text-white"
          data-testid="message-input"
        />
        <button
          type="submit"
          className="px-4 rounded-full gradient-bg text-white text-sm inline-flex items-center gap-1 hover:scale-[1.02] transition-transform"
          data-testid="message-send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

function PDITile({ unlocked, sessions, onStart }) {
  const inProgress = sessions?.find((s) => s.status === "in_progress");
  return (
    <div className="rounded-3xl border border-white/5 bg-surface p-6 md:p-8 relative overflow-hidden" data-testid="pdi-tile">
      <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full opacity-25 blur-3xl gradient-bg pointer-events-none" />
      <div className="relative flex items-center justify-between">
        <div>
          <p className="eyebrow gradient-text">Remote PDI</p>
          <h3 className="mt-2 font-serif text-2xl text-white">24-point guided inspection</h3>
        </div>
        <Wrench className="w-5 h-5 text-silver/50" />
      </div>
      {unlocked ? (
        <>
          <p className="mt-4 text-sm text-silver/70 leading-relaxed">
            Walk the showroom with our checklist. Every checkpoint captures a photo + note. We generate a signed PDF report at the end.
          </p>
          {inProgress ? (
            <Link to={`/pdi/${inProgress.id}`} className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.02] transition-transform" data-testid="pdi-resume">
              Resume in-progress PDI
            </Link>
          ) : (
            <button onClick={onStart} className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.02] transition-transform" data-testid="pdi-start">
              Start Remote PDI
            </button>
          )}
          {sessions?.length > 0 && (
            <ul className="mt-6 divide-y divide-white/5 text-sm">
              {sessions.map((s) => (
                <li key={s.id} className="py-2 flex items-center justify-between">
                  <span className="text-silver/70">{new Date(s.created_at).toLocaleDateString()} · {s.status}</span>
                  <Link to={`/pdi/${s.id}`} className="text-xs text-white underline">open</Link>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          <p className="mt-4 text-sm text-silver/70 leading-relaxed">
            Available on Arjuna, Bhishma & Madhav tiers. Ask your Saarthi to unlock this for you.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-xs text-silver/60" data-testid="pdi-locked-badge">
            Locked · contact your Saarthi
          </div>
        </>
      )}
    </div>
  );
}

function ReferralCard({ referrals, onCopy, onCopyLink, onShare, onRequestPayout }) {
  if (!referrals) return null;
  const wallet = referrals.wallet || { earned: 0, available: 0 };
  const remaining = referrals.remaining_slots ?? 0;
  const successful = referrals.successful_referrals ?? 0;
  const max = referrals.max_successful_referrals ?? 5;
  const link = buildReferralLink(referrals.code);
  return (
    <div className="rounded-3xl gradient-border bg-[#141323] p-6 md:p-8 relative overflow-hidden" data-testid="referral-card">
      <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full opacity-30 blur-3xl gradient-bg pointer-events-none" />
      <div className="relative flex items-center justify-between">
        <div>
          <p className="eyebrow gradient-text">Refer a buyer</p>
          <h3 className="mt-2 font-serif text-2xl text-white">Earn ₹1,000 when they close.</h3>
          <p className="mt-1 text-xs text-silver/50">You can refer up to 5 people. Earn ₹1,000 for each referred buyer whose CaarSaarthi deal is Closed.</p>
        </div>
        <Gift className="w-5 h-5 text-silver/50 shrink-0" />
      </div>

      <div className="relative mt-6 flex items-center gap-3">
        <div className="flex-1 rounded-2xl border border-white/10 bg-void px-5 py-4 font-mono text-lg text-white tracking-widest" data-testid="referral-code">{referrals.code}</div>
        <button onClick={onCopy} className="p-3 rounded-full border border-white/10 hover:bg-white/5 text-silver hover:text-white" data-testid="referral-copy" title="Copy code">
          <Copy className="w-4 h-4" />
        </button>
      </div>

      <div className="relative mt-3 flex items-center gap-3">
        <div className="flex-1 rounded-2xl border border-white/10 bg-void px-5 py-3 text-xs text-silver/70 truncate" data-testid="referral-link">{link}</div>
        <button onClick={onCopyLink} className="p-3 rounded-full border border-white/10 hover:bg-white/5 text-silver hover:text-white" data-testid="referral-copy-link" title="Copy link">
          <Copy className="w-4 h-4" />
        </button>
        <button onClick={onShare} className="px-4 py-3 rounded-full text-xs font-medium text-white gradient-bg hover:scale-[1.02] transition-transform" data-testid="referral-share">
          Share
        </button>
      </div>

      <div className="relative mt-6 grid grid-cols-3 gap-3 text-center">
        <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
          <div className="font-serif text-2xl text-white">{referrals.signups}</div>
          <div className="text-[11px] uppercase tracking-widest text-silver/50">Signed up</div>
        </div>
        <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
          <div className="font-serif text-2xl text-white">{successful}/{max}</div>
          <div className="text-[11px] uppercase tracking-widest text-silver/50">Closed referrals</div>
        </div>
        <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
          <div className="font-serif text-2xl text-white">₹{(wallet.available || 0).toLocaleString("en-IN")}</div>
          <div className="text-[11px] uppercase tracking-widest text-silver/50">Available credit</div>
        </div>
      </div>

      <p className="relative mt-4 text-xs text-silver/50">
        {remaining > 0
          ? `${remaining} successful referral${remaining === 1 ? "" : "s"} remaining.`
          : "You have reached the 5-referral reward limit."}
        {" "}Total earned: ₹{(wallet.earned || 0).toLocaleString("en-IN")}.
      </p>

      {wallet.available >= 1000 && (
        <button
          onClick={() => onRequestPayout(wallet.available - (wallet.available % 1000))}
          className="relative mt-4 w-full py-3 rounded-full text-sm font-medium border border-white/10 text-white hover:bg-white/5"
          data-testid="referral-request-payout"
        >
          Request payout of ₹{(wallet.available - (wallet.available % 1000)).toLocaleString("en-IN")}
        </button>
      )}

      <div className="relative mt-6 pt-5 border-t border-white/5 text-[11px] leading-relaxed text-silver/40">
        ₹1,000 is earned only when the referred buyer's deal is marked Closed. Maximum 5 successful referrals.
        Earned credits may be used against CaarSaarthi services or requested as a payout, subject to verification and approval.
      </div>
    </div>
  );
}

